import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      nav: {
        dashboard: 'Dashboard',
        clients: 'Clients',
        settings: 'Settings',
        profile: 'Profile',
        logout: 'Log Out',
        login: 'Log In',
        signup: 'Sign Up',
        language: 'Language',
        english: 'English',
        spanish: 'Spanish',
      },
      dashboard: {
        welcome: 'Executive Dashboard',
        subtitle: 'Overview of your lending portfolio',
        totalLoans: 'Total Loans',
        totalLoanValue: 'Total Loan Value',
        avgRiskScore: 'Avg Risk Score',
        defaultRate: 'Default Rate',
        active: 'active',
        portfolioValue: 'Portfolio value',
        lowRisk: 'Low risk',
        mediumRisk: 'Medium risk',
        highRisk: 'High risk',
        healthy: 'Healthy',
        attentionNeeded: 'Attention needed',
        monthlyTrendsTitle: 'Monthly Trends',
        monthlyTrendsDesc: 'Loans and revenue over time',
        loansSeries: 'Loans',
        revenueSeries: 'Revenue ($)',
        riskDistributionTitle: 'Risk Distribution',
        riskDistributionDesc: 'Client risk categories',
        revenueSummaryTitle: 'Revenue Summary',
        revenueSummaryDesc: 'Financial performance metrics',
        totalRevenue: 'Total Revenue',
        monthlyRevenue: 'Monthly Revenue',
        yieldRate: 'Yield Rate',
        loanTypesTitle: 'Loan Types',
        loanTypesDesc: 'Distribution by loan category',
        countSeries: 'Count',
        noDataTitle: 'No Data Yet',
        noDataDesc: 'Start by adding clients to explore the dashboard features.',
        addFirstClient: 'Add First Client',
        noTrendData: 'No trend data available',
        noDistributionData: 'No distribution data available',
        noLoanTypeData: 'No loan type data available',
      },
      clients: {
        title: 'Clients',
        subtitle: 'total clients',
        searchPlaceholder: 'Search clients by name, email or company...',
        filterStatus: 'Filter by status',
        allStatuses: 'All Statuses',
        active: 'Active',
        pending: 'Pending',
        defaulted: 'Defaulted',
        closed: 'Closed',
        review: 'In Review',
        filterRisk: 'Filter by risk',
        allRisk: 'All Risk Levels',
        lowRisk: 'Low',
        mediumRisk: 'Medium',
        highRisk: 'High',
        addClient: 'Add Client',
        colName: 'Client Name',
        colCompany: 'Company',
        colStatus: 'Status',
        colLoans: 'Active Loans',
        colPortfolio: 'Total Portfolio',
        colRisk: 'Risk Score',
        colActions: 'Actions',
        viewProfile: 'View Profile',
        editClient: 'Edit Client',
        deleteClient: 'Delete Client',
        noClientsFound: 'No clients found matching your search criteria.',
        modalTitle: 'Add New Client',
        step1Title: 'Step 1: Select the category of client/taxpayer',
        step2Title: 'Step 2: Enter the client information below',
        clientTypeLabel: 'Client Category / Taxpayer Type',
        personaMoral: 'Persona Moral (Corporation / Company)',
        personaFisicaEmp: 'Persona Física con Actividad Empresarial (Sole Proprietorship)',
        personaFisica: 'Persona Física (Individual / Employee)',
        firstName: 'First Name',
        lastName: 'Last Name',
        middleName: 'Middle / Second Name',
        fiscalId: 'Fiscal ID / RFC',
        fiscalRegime: 'Fiscal Regime',
        fiscalAddress: 'Fiscal Address',
        email: 'Email Address',
        phone: 'Phone Number',
        city: 'City',
        state: 'State',
        zipCode: 'ZIP Code',
        exteriorNumber: 'Exterior Number',
        interiorNumber: 'Interior Number',
        neighborhood: 'Neighborhood / Colonia',
        municipality: 'Municipality / Alcaldía',
        nationality: 'Nationality',
        country: 'Country',
        initialLoan: 'Initial Loan Amount',
        loanType: 'Loan Type',
        riskScore: 'Risk Score (0-100)',
        cancel: 'Cancel',
        next: 'Next',
        back: 'Back',
        save: 'Save Client',
        saving: 'Saving client...',
        successAdd: 'Client created successfully!',
        // Loan type translations
        workingCapital: 'Working Capital',
        businessExpansion: 'Business Expansion',
        equipmentFinancing: 'Equipment Financing',
        fleetFinancing: 'Fleet Financing',
        projectFinancing: 'Project Financing',
        renovationLoan: 'Renovation Loan',
        // Industry translations
        technology: 'Technology',
        healthcare: 'Healthcare',
        finance: 'Finance',
        retail: 'Retail',
        manufacturing: 'Manufacturing',
        realEstate: 'Real Estate',
      },
      clientDetail: {
        backToClients: 'Back to Clients',
        clientProfile: 'Client Profile',
        contactInfo: 'Contact Information',
        financialOverview: 'Financial Overview',
        totalPortfolio: 'Total Portfolio',
        activeLoans: 'Active Loans',
        riskLevel: 'Risk Level',
        tabOverview: 'Overview & AI Summary',
        tabAnalytics: 'Graphics & Analytics',
        tabScoring: 'FinAI & Ad-Hoc Scoring',
        tabLoans: 'Loan History',
        tabDocuments: 'Documents',
        tabAi: 'AI Summary & Insights',
        generateAiSummary: 'Generate Executive Summary',
        exportPdf: 'Export Summary as PDF',
        generating: 'Generating AI analysis...',
        // Overview card labels
        loanDetails: 'Loan Details',
        loanAmount: 'Loan Amount',
        loanType: 'Loan Type',
        industry: 'Industry',
        created: 'Created',
        companyInfo: 'Company Information',
        annualRevenue: 'Annual Revenue',
        employees: 'Employees',
        riskAssessment: 'Risk Assessment',
        riskScore: 'Risk Score',
        aiSummary: 'AI Summary',
        aiSummaryDesc: 'Generate an executive summary with AI',
        generateSummaryBtn: 'Generate Summary',
        generatingBtn: 'Generating...',
        downloadPdfBtn: 'Download as PDF',
        creatingPdfBtn: 'Creating PDF...',
        executiveSummaryTitle: 'Executive Summary',
        generatedAt: 'Generated',
        // Analytics tab
        dscrLabel: 'Debt Service Coverage (DSCR)',
        dscrStatus: 'Healthy Coverage',
        workingCapitalLabel: 'Estimated Working Capital',
        workingCapitalStatus: 'Strong Liquidity',
        onTimePaymentLabel: 'On-Time Payment Record',
        onTimePaymentStatus: '0 Late Payments',
        dtiLabel: 'Debt-to-Income Ratio',
        dtiStatus: 'Manageable Ratio',
        monthlyTrendsChartTitle: '6-Month Revenue & Repayment Trends',
        monthlyTrendsChartDesc: 'Monthly revenue vs scheduled loan repayments',
        revenueSeriesLabel: 'Monthly Revenue',
        repaymentSeriesLabel: 'Loan Repayment',
        riskDimensionTitle: 'Risk Dimension Scores',
        riskDimensionDesc: 'Sub-scores evaluated across risk categories',
        categoryScoreLabel: 'Category Score',
        recentTransactionsTitle: 'Recent Transaction & Payment History',
        recentTransactionsDesc: 'Comprehensive ledger of recent payments',
        colTxId: 'Transaction ID',
        colDate: 'Date',
        colType: 'Type',
        colMethod: 'Payment Method',
        colAmount: 'Amount',
        colStatus: 'Status',
        // Scoring tab
        scoringHeaderTitle: 'Client Credit & Risk Assessment',
        scoringHeaderDesc: 'Comparing automated FinAI Score against custom evaluator parameters',
        standardScoreLabel: 'Standard FinAI Score',
        outOf100: 'Out of 100',
        customAdHocScoreLabel: 'Custom Ad-Hoc Score',
        adjustedScore: 'Adjusted Score',
        favorablePts: 'pts (Favorable)',
        conservativePts: 'pts (Conservative)',
        finAiParametersTitle: 'FinAI Standard Calculation Parameters',
        finAiParametersDesc: 'Algorithmic weights and inputs used to derive the official FinAI Score',
        creditBureauIndex: 'Credit Bureau Index',
        weight: 'Weight',
        creditBureauDesc: 'Calculated from credit agency reports',
        dscrDesc: 'Annual Cash Flow / Annual Debt Obligations',
        liquidityRatio: 'Liquidity & Cash Reserve Ratio',
        liquidityDesc: 'Liquid assets vs monthly operational burn',
        historicalDefaultRate: 'Historical Default Rate',
        historicalDefaultDesc: 'Past defaults across banking partners',
        industrySectorRisk: 'Industry Sector Risk',
        adHocTitle: 'Custom Ad-Hoc Evaluator',
        adHocDesc: 'Adjust evaluation parameters manually to simulate customized risk scenarios',
        managementExp: 'Management Experience Level',
        collateralQuality: 'Collateral & Asset Backing',
        marketConditions: 'Market Position & Outlook',
        exceptional: 'Exceptional',
        experienced: 'Experienced',
        limited: 'Limited',
        fullySecured: 'Fully Secured',
        partial: 'Partial',
        unsecured: 'Unsecured',
        cpaAudit: 'Independent CPA / Big 4 Audit',
        cpaAuditDesc: 'Verified financial statements (+5 pts)',
        parentGuarantee: 'Parent / Corporate Guarantee',
        parentGuaranteeDesc: 'Third-party guarantee active (+10 pts)',
        esgCert: 'ESG & Compliance Certification',
        esgCertDesc: 'Regulatory & governance compliant (+5 pts)',
        notesLabel: 'Ad-Hoc Assessment Rationale / Notes',
        notesPlaceholder: 'Enter justification notes for custom score adjustments...',
        saveEvaluationBtn: 'Save Ad-Hoc Evaluation',
        savedBadge: 'Saved!',
      },
      settings: {
        title: 'Settings',
        subtitle: 'Manage your account, subscription, and preferences',
        navAccount: 'Account',
        navApi: 'External APIs',
        navBilling: 'Billing',
        navCompany: 'Company',
        navInvoicing: 'Invoices',
        navNotifications: 'Notifications',
        navSubscription: 'Subscription',
        saveChanges: 'Save Changes',
        savedSuccess: 'Settings saved successfully!',
        accountTitle: 'Account Settings',
        accountSubtitle: 'Manage your personal profile and account credentials.',
        fullName: 'Full Name',
        emailAddress: 'Email Address',
        phone: 'Phone Number',
        passwordTitle: 'Change Password',
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        companyTitle: 'Company Information',
        companySubtitle: 'Manage your business profile and branding.',
        companyName: 'Company Name',
        taxId: 'Tax ID / RFC',
        companyLogo: 'Company Logo',
        uploadLogo: 'Upload Logo',
        removeLogo: 'Remove',
      },
      auth: {
        loginTitle: 'Welcome Back',
        loginSubtitle: 'Enter your credentials to access your account',
        email: 'Email Address',
        password: 'Password',
        rememberMe: 'Remember me',
        forgotPassword: 'Forgot password?',
        signInBtn: 'Sign In',
        noAccount: "Don't have an account?",
        signUpLink: 'Sign Up',
        signupTitle: 'Create an Account',
        signupSubtitle: 'Get started with Pleiades platform today',
        fullName: 'Full Name',
        confirmPassword: 'Confirm Password',
        agreeTerms: 'I agree to the Terms of Service and Privacy Policy',
        createAccountBtn: 'Create Account',
        alreadyHaveAccount: 'Already have an account?',
        logInLink: 'Log In',
      },
      common: {
        loading: 'Loading...',
        save: 'Save',
        cancel: 'Cancel',
        edit: 'Edit',
        delete: 'Delete',
        search: 'Search',
        filter: 'Filter',
        success: 'Success',
        error: 'Error',
        confirm: 'Confirm',
        back: 'Back',
        actions: 'Actions',
        status: 'Status',
      },
    },
  },
  es: {
    translation: {
      nav: {
        dashboard: 'Panel Principal',
        clients: 'Clientes',
        settings: 'Configuración',
        profile: 'Perfil',
        logout: 'Cerrar Sesión',
        login: 'Iniciar Sesión',
        signup: 'Registrarse',
        language: 'Idioma',
        english: 'Inglés',
        spanish: 'Español',
      },
      dashboard: {
        welcome: 'Panel Ejecutivo',
        subtitle: 'Resumen del estado general de tu portafolio de créditos',
        totalLoans: 'Total de Préstamos',
        totalLoanValue: 'Valor Total del Portafolio',
        avgRiskScore: 'Score Promedio de Riesgo',
        defaultRate: 'Tasa de Morosidad',
        active: 'activos',
        portfolioValue: 'Valor del portafolio',
        lowRisk: 'Riesgo bajo',
        mediumRisk: 'Riesgo medio',
        highRisk: 'Riesgo alto',
        healthy: 'Saludable',
        attentionNeeded: 'Atención requerida',
        monthlyTrendsTitle: 'Tendencias Mensuales',
        monthlyTrendsDesc: 'Préstamos e ingresos a lo largo del tiempo',
        loansSeries: 'Préstamos',
        revenueSeries: 'Ingresos ($)',
        riskDistributionTitle: 'Distribución de Riesgo',
        riskDistributionDesc: 'Categorías de riesgo de clientes',
        revenueSummaryTitle: 'Resumen de Ingresos',
        revenueSummaryDesc: 'Métricas de rendimiento financiero',
        totalRevenue: 'Ingresos Totales',
        monthlyRevenue: 'Ingresos Mensuales',
        yieldRate: 'Tasa de Rendimiento',
        loanTypesTitle: 'Tipos de Préstamos',
        loanTypesDesc: 'Distribución por categoría de crédito',
        countSeries: 'Cantidad',
        noDataTitle: 'Sin Datos Aún',
        noDataDesc: 'Comienza agregando clientes para explorar las funciones del panel.',
        addFirstClient: 'Agregar Primer Cliente',
        noTrendData: 'No hay datos de tendencias disponibles',
        noDistributionData: 'No hay datos de distribución disponibles',
        noLoanTypeData: 'No hay datos de tipos de préstamo disponibles',
      },
      clients: {
        title: 'Clientes',
        subtitle: 'clientes en total',
        searchPlaceholder: 'Buscar clientes por nombre, correo o empresa...',
        filterStatus: 'Filtrar por estado',
        allStatuses: 'Todos los estados',
        active: 'Activo',
        pending: 'Pendiente',
        defaulted: 'En Mora',
        closed: 'Cerrado',
        review: 'En Revisión',
        filterRisk: 'Filtrar por riesgo',
        allRisk: 'Todos los niveles',
        lowRisk: 'Bajo',
        mediumRisk: 'Medio',
        highRisk: 'Alto',
        addClient: 'Agregar Cliente',
        colName: 'Nombre del Cliente',
        colCompany: 'Empresa',
        colStatus: 'Estado',
        colLoans: 'Préstamos Activos',
        colPortfolio: 'Portafolio Total',
        colRisk: 'Score de Riesgo',
        colActions: 'Acciones',
        viewProfile: 'Ver Perfil',
        editClient: 'Editar Cliente',
        deleteClient: 'Eliminar Cliente',
        noClientsFound: 'No se encontraron clientes que coincidan con la búsqueda.',
        modalTitle: 'Agregar Nuevo Cliente',
        step1Title: 'Paso 1: Selecciona la categoría del cliente / contribuyente',
        step2Title: 'Paso 2: Ingresa la información del cliente a continuación',
        clientTypeLabel: 'Categoría de Cliente / Tipo de Contribuyente',
        personaMoral: 'Persona Moral (Empresa / Sociedad)',
        personaFisicaEmp: 'Persona Física con Actividad Empresarial',
        personaFisica: 'Persona Física (Particular / Empleado)',
        firstName: 'Nombre',
        lastName: 'Apellido Paterno',
        middleName: 'Segundo Nombre / Apellido Materno',
        fiscalId: 'RFC / Identificación Fiscal',
        fiscalRegime: 'Régimen Fiscal',
        fiscalAddress: 'Domicilio Fiscal',
        email: 'Correo Electrónico',
        phone: 'Teléfono',
        city: 'Ciudad',
        state: 'Estado',
        zipCode: 'Código Postal',
        exteriorNumber: 'Número Exterior',
        interiorNumber: 'Número Interior',
        neighborhood: 'Colonia',
        municipality: 'Municipio / Alcaldía',
        nationality: 'Nacionalidad',
        country: 'País',
        initialLoan: 'Monto del Préstamo Inicial',
        loanType: 'Tipo de Préstamo',
        riskScore: 'Score de Riesgo (0-100)',
        cancel: 'Cancelar',
        next: 'Siguiente',
        back: 'Atrás',
        save: 'Guardar Cliente',
        saving: 'Guardando cliente...',
        successAdd: '¡Cliente creado exitosamente!',
        // Loan type translations
        workingCapital: 'Capital de Trabajo',
        businessExpansion: 'Expansión de Negocio',
        equipmentFinancing: 'Financiamiento de Equipo',
        fleetFinancing: 'Financiamiento de Flotilla',
        projectFinancing: 'Financiamiento de Proyecto',
        renovationLoan: 'Préstamo de Remodelación',
        // Industry translations
        technology: 'Tecnología',
        healthcare: 'Salud',
        finance: 'Finanzas',
        retail: 'Comercio / Retail',
        manufacturing: 'Manufactura',
        realEstate: 'Bienes Raíces',
      },
      clientDetail: {
        backToClients: 'Volver a Clientes',
        clientProfile: 'Perfil del Cliente',
        contactInfo: 'Información de Contacto',
        financialOverview: 'Resumen Financiero',
        totalPortfolio: 'Portafolio Total',
        activeLoans: 'Préstamos Activos',
        riskLevel: 'Nivel de Riesgo',
        tabOverview: 'Resumen y Resumen IA',
        tabAnalytics: 'Gráficas y Analítica',
        tabScoring: 'Scoring FinAI y Ad-Hoc',
        tabLoans: 'Historial de Préstamos',
        tabDocuments: 'Documentos',
        tabAi: 'Resumen e Insights IA',
        generateAiSummary: 'Generar Resumen Ejecutivo',
        exportPdf: 'Exportar Resumen a PDF',
        generating: 'Generando análisis IA...',
        // Overview card labels
        loanDetails: 'Detalles del Préstamo',
        loanAmount: 'Monto del Préstamo',
        loanType: 'Tipo de Préstamo',
        industry: 'Industria',
        created: 'Fecha de Creación',
        companyInfo: 'Información de la Empresa',
        annualRevenue: 'Ingresos Anuales',
        employees: 'Empleados',
        riskAssessment: 'Evaluación de Riesgo',
        riskScore: 'Score de Riesgo',
        aiSummary: 'Resumen IA',
        aiSummaryDesc: 'Generar un resumen ejecutivo con IA',
        generateSummaryBtn: 'Generar Resumen',
        generatingBtn: 'Generando...',
        downloadPdfBtn: 'Descargar en PDF',
        creatingPdfBtn: 'Creando PDF...',
        executiveSummaryTitle: 'Resumen Ejecutivo',
        generatedAt: 'Generado el',
        // Analytics tab
        dscrLabel: 'Cobertura de Servicio de Deuda (DSCR)',
        dscrStatus: 'Cobertura Saludable',
        workingCapitalLabel: 'Capital de Trabajo Estimado',
        workingCapitalStatus: 'Alta Liquidez',
        onTimePaymentLabel: 'Registro de Pagos a Tiempo',
        onTimePaymentStatus: '0 Pagos Atrasados',
        dtiLabel: 'Relación Deuda e Ingresos',
        dtiStatus: 'Proporción Manejable',
        monthlyTrendsChartTitle: 'Tendencias de Ingresos y Pagos (6 Meses)',
        monthlyTrendsChartDesc: 'Ingresos mensuales vs pagos programados',
        revenueSeriesLabel: 'Ingresos Mensuales',
        repaymentSeriesLabel: 'Pago de Préstamo',
        riskDimensionTitle: 'Puntuaciones por Dimensión de Riesgo',
        riskDimensionDesc: 'Sub-puntuaciones evaluadas por categoría',
        categoryScoreLabel: 'Puntaje por Categoría',
        recentTransactionsTitle: 'Historial Reciente de Transacciones y Pagos',
        recentTransactionsDesc: 'Libro contable de pagos recientes',
        colTxId: 'ID de Transacción',
        colDate: 'Fecha',
        colType: 'Tipo',
        colMethod: 'Método de Pago',
        colAmount: 'Monto',
        colStatus: 'Estado',
        // Scoring tab
        scoringHeaderTitle: 'Evaluación de Crédito y Riesgo del Cliente',
        scoringHeaderDesc: 'Comparativa entre Score FinAI automatizado y parámetros evaluadores personalizados',
        standardScoreLabel: 'Score Estándar FinAI',
        outOf100: 'De 100',
        customAdHocScoreLabel: 'Score Ad-Hoc Personalizado',
        adjustedScore: 'Puntaje Ajustado',
        favorablePts: 'pts (Favorable)',
        conservativePts: 'pts (Conservador)',
        finAiParametersTitle: 'Parámetros del Cálculo Estándar FinAI',
        finAiParametersDesc: 'Pesos algorítmicos e insumos para derivar el Score FinAI oficial',
        creditBureauIndex: 'Índice de Buró de Crédito',
        weight: 'Peso',
        creditBureauDesc: 'Calculado a partir de reportes de agencias crediticias',
        dscrDesc: 'Flujo de Caja Anual / Obligaciones de Deuda Anual',
        liquidityRatio: 'Ratio de Liquidez y Reserva de Caja',
        liquidityDesc: 'Activos líquidos vs gasto operativo mensual',
        historicalDefaultRate: 'Tasa Histórica de Incumplimiento',
        historicalDefaultDesc: 'Incumplimientos pasados entre socios bancarios',
        industrySectorRisk: 'Riesgo del Sector Industrial',
        adHocTitle: 'Evaluador Personalizado Ad-Hoc',
        adHocDesc: 'Ajusta manualmente los parámetros para simular escenarios de riesgo personalizados',
        managementExp: 'Nivel de Experiencia Directiva',
        collateralQuality: 'Garantías y Respaldo de Activos',
        marketConditions: 'Posición y Perspectiva de Mercado',
        exceptional: 'Excepcional',
        experienced: 'Experimentado',
        limited: 'Limitado',
        fullySecured: 'Totalmente Garantizado',
        partial: 'Parcial',
        unsecured: 'Sin Garantía',
        cpaAudit: 'Auditoría CPA Independiente / Big 4',
        cpaAuditDesc: 'Estados financieros dictaminados (+5 pts)',
        parentGuarantee: 'Garantía Corporativa / Matriz',
        parentGuaranteeDesc: 'Garantía de terceros activa (+10 pts)',
        esgCert: 'Certificación ESG y Cumplimiento',
        esgCertDesc: 'Cumplimiento normativo y gobernanza (+5 pts)',
        notesLabel: 'Justificación / Notas de la Evaluación Ad-Hoc',
        notesPlaceholder: 'Ingresa notas de justificación para ajustes personalizados...',
        saveEvaluationBtn: 'Guardar Evaluación Ad-Hoc',
        savedBadge: '¡Guardado!',
      },
      settings: {
        title: 'Configuración',
        subtitle: 'Gestiona la configuración de tu cuenta, suscripción y preferencias',
        navAccount: 'Cuenta',
        navApi: 'APIs Externas',
        navBilling: 'Facturación',
        navCompany: 'Empresa',
        navInvoicing: 'Facturas',
        navNotifications: 'Notificaciones',
        navSubscription: 'Suscripción',
        saveChanges: 'Guardar Cambios',
        savedSuccess: '¡Configuración guardada exitosamente!',
        accountTitle: 'Configuración de la Cuenta',
        accountSubtitle: 'Gestiona tu perfil personal y credenciales.',
        fullName: 'Nombre Completo',
        emailAddress: 'Correo Electrónico',
        phone: 'Teléfono',
        passwordTitle: 'Cambiar Contraseña',
        currentPassword: 'Contraseña Actual',
        newPassword: 'Nueva Contraseña',
        confirmPassword: 'Confirmar Nueva Contraseña',
        companyTitle: 'Información de la Empresa',
        companySubtitle: 'Gestiona los datos de tu negocio e imagen institucional.',
        companyName: 'Nombre Legal / Razón Social',
        taxId: 'RFC / Identificación Fiscal',
        companyLogo: 'Logotipo de la Empresa',
        uploadLogo: 'Subir Logotipo',
        removeLogo: 'Eliminar',
      },
      auth: {
        loginTitle: 'Bienvenido de Nuevo',
        loginSubtitle: 'Ingresa tus credenciales para acceder a tu cuenta',
        email: 'Correo Electrónico',
        password: 'Contraseña',
        rememberMe: 'Recordarme',
        forgotPassword: '¿Olvidaste tu contraseña?',
        signInBtn: 'Iniciar Sesión',
        noAccount: '¿No tienes una cuenta?',
        signUpLink: 'Regístrate',
        signupTitle: 'Crear una Cuenta',
        signupSubtitle: 'Comienza hoy mismo con la plataforma Pleiades',
        fullName: 'Nombre Completo',
        confirmPassword: 'Confirmar Contraseña',
        agreeTerms: 'Acepto los Términos de Servicio y la Política de Privacidad',
        createAccountBtn: 'Crear Cuenta',
        alreadyHaveAccount: '¿Ya tienes una cuenta?',
        logInLink: 'Inicia Sesión',
      },
      common: {
        loading: 'Cargando...',
        save: 'Guardar',
        cancel: 'Cancelar',
        edit: 'Editar',
        delete: 'Eliminar',
        search: 'Buscar',
        filter: 'Filtrar',
        success: 'Éxito',
        error: 'Error',
        confirm: 'Confirmar',
        back: 'Volver',
        actions: 'Acciones',
        status: 'Estado',
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'es',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

export default i18n;
